import {
  ComponentPropsWithoutRef,
  ElementType,
  MouseEventHandler,
} from "react";
import { cva } from "../../../styled-system/css";

const chips = cva({
  base: {
    display: "inline-flex",
    borderRadius: "16px",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    wordBreak: "keep-all",
  },
  variants: {
    variant: {
      primaryBaseContained: {
        bgColor: "primary.base",
        color: "primary.contrast",
        border: "none",
      },
      backgroundSecondaryContained: {
        bgColor: "background.secondary",
        color: "text.primary",
        border: "none",
      },
      primaryDisabledContained: {
        bgColor: "primary.disabled",
        color: "text.disabled",
      },
      primaryLightenContained: {
        bgColor: "primary.lighten",
        color: "text.contrast",
      },
      backgroundDarkenContained: {
        bgColor: "background.darken",
        color: "text.primary",
      },
      borderPrimaryOutlined: {
        bgColor: "primary.contrast",
        color: "text.primary",
        borderWidth: "1px",
        borderStyle: "solid",
        borderColor: "border.primary",
      },
      borderSecondaryOutlined: {
        bgColor: "primary.contrast",
        color: "text.primary",
        borderWidth: "1px",
        borderStyle: "solid",
        borderColor: "border.secondary",
      },
      secondaryBaseOutlined: {
        bgColor: "primary.contrast",
        color: "secondary.base",
        borderWidth: "1px",
        borderStyle: "solid",
        borderColor: "secondary.base",
      },
    },
    size: {
      xl: {
        height: "32px",
      },
      l: {
        height: "30px",
      },
      m: {
        height: "26px",
      },
      s: {
        height: "24px",
      },
    },
    fontWeight: {
      bold: { fontWeight: "bold" },
      regular: { fontWeight: "regular" },
    },
    hasCheckIcon: {
      true: {},
      false: {},
    },
    hasCloseIcon: {
      true: {},
      false: {},
    },
  },
  compoundVariants: [
    {
      hasCheckIcon: false,
      hasCloseIcon: false,
      size: ["xl", "l", "m"],
      css: {
        paddingLeft: "16px",
        paddingRight: "16px",
      },
    },
    {
      hasCheckIcon: false,
      hasCloseIcon: false,
      size: "s",
      css: {
        paddingLeft: "12px",
        paddingRight: "12px",
      },
    },
    {
      hasCheckIcon: true,
      hasCloseIcon: false,
      size: ["xl", "l", "m"],
      css: {
        paddingLeft: "4px",
        paddingRight: "16px",
      },
    },
    {
      hasCheckIcon: true,
      hasCloseIcon: false,
      size: "s",
      css: {
        paddingLeft: "4px",
        paddingRight: "12px",
      },
    },
    {
      hasCheckIcon: false,
      hasCloseIcon: true,
      size: ["xl", "l", "m"],
      css: {
        paddingLeft: "12px",
        paddingRight: "8px",
      },
    },
    {
      hasCheckIcon: false,
      hasCloseIcon: true,
      size: "s",
      css: {
        paddingLeft: "12px",
        paddingRight: "4px",
      },
    },
  ],
});

const textStyle = cva({
  variants: {
    size: {
      xl: {
        textStyle: "button.s",
      },
      l: {
        textStyle: "button.s",
      },
      m: {
        textStyle: "button.xs",
      },
      s: {
        textStyle: "button.xs",
      },
    },
    fontWeight: {
      bold: { fontWeight: "bold" },
      regular: { fontWeight: "regular" },
    },
  },
});

type stateProps =
  | {
      isChecked?: boolean;
      isCloseable?: undefined;
    }
  | {
      isChecked?: undefined;
      isCloseable?: boolean;
    };

interface TextProps {
  text: string;
  fontWeight: "bold" | "regular";
  onClick?: MouseEventHandler<HTMLElement>;
}

type VisualProps =
  | {
      size: "xl";
      variant:
        | "primaryBaseContained"
        | "backgroundSecondaryContained"
        | "borderPrimaryOutlined"
        | "borderSecondaryOutlined";
    }
  | {
      size: "l";
      variant:
        | "primaryBaseContained"
        | "backgroundSecondaryContained"
        | "borderPrimaryOutlined"
        | "borderSecondaryOutlined"
        | "secondaryBaseOutlined"
        | "backgroundDarkenContained";
    }
  | {
      size: "m";
      variant:
        | "primaryBaseContained"
        | "backgroundSecondaryContained"
        | "borderPrimaryOutlined"
        | "borderSecondaryOutlined";
    }
  | {
      size: "s";
      variant:
        | "primaryBaseContained"
        | "backgroundSecondaryContained"
        | "primaryDisabledContained"
        | "primaryLightenContained"
        | "borderPrimaryOutlined"
        | "borderSecondaryOutlined";
    };

type ChipsProps<T extends ElementType> = {
  tag?: T;
} & Omit<ComponentPropsWithoutRef<T>, "tag"> &
  TextProps &
  VisualProps &
  stateProps;

export function Chips<T extends ElementType>({
  tag,
  text,
  variant,
  size,
  fontWeight,
  isChecked,
  isCloseable,
  onClick,
}: ChipsProps<T>) {
  const Tag = tag ?? "div";

  return (
    <Tag
      className={chips({
        variant,
        size,
        fontWeight,
        hasCheckIcon: !!isChecked,
        hasCloseIcon: !!isCloseable,
      })}
      onClick={onClick}
    >
      <span
        className={textStyle({
          size: size,
          fontWeight,
        })}
      >
        {text}
      </span>
    </Tag>
  );
}
